import type { components } from "@/shared/lib/api.types";
import { todayIso } from "@/shared/utils/date";
import type { PatchFormInput, PatchFormValues } from "./settings.patch.schemas";

export type PatchResponse = components["schemas"]["PatchResponse"];

export type ReferencedRemoval =
  | {
      kind: "raid";
      name: string;
      raidDayCount: number;
      bossViewCount: number;
    }
  | {
      kind: "boss";
      name: string;
      raidName: string;
      bossViewCount: number;
      assignmentCount: number;
    };

export function createEmptyPatch(): PatchFormInput {
  const today = todayIso();
  return {
    name: "",
    startsOn: today,
    endsOn: today,
    vaultThreshold1: 2,
    vaultThreshold2: 4,
    vaultThreshold3: 6,
    raids: [],
  };
}

export function patchToFormInput(patch: PatchResponse): PatchFormInput {
  return {
    name: patch.name,
    startsOn: patch.startsOn,
    endsOn: patch.endsOn,
    vaultThreshold1: Number(patch.vaultThreshold1),
    vaultThreshold2: Number(patch.vaultThreshold2),
    vaultThreshold3: Number(patch.vaultThreshold3),
    raids: [...patch.raids]
      .sort(
        (left, right) => Number(left.sortPosition) - Number(right.sortPosition),
      )
      .map((raid) => ({
        id: raid.id,
        name: raid.name,
        bosses: [...raid.bosses]
          .sort(
            (left, right) =>
              Number(left.sortPosition) - Number(right.sortPosition),
          )
          .map((boss) => ({ id: boss.id, name: boss.name })),
      })),
  };
}

export function findReferencedRemovals(
  baseline: PatchResponse,
  draft: PatchFormValues,
): ReferencedRemoval[] {
  const retainedRaidIds = new Set(
    draft.raids.flatMap((raid) => (raid.id ? [raid.id] : [])),
  );
  const retainedBossIds = new Set(
    draft.raids.flatMap((raid) =>
      raid.bosses.flatMap((boss) => (boss.id ? [boss.id] : [])),
    ),
  );

  const removals: ReferencedRemoval[] = [];
  for (const raid of baseline.raids) {
    const raidDayCount = Number(raid.raidDayReferenceCount);
    const raidBossViewCount = Number(raid.bossViewCount);
    if (
      !retainedRaidIds.has(raid.id) &&
      (raidDayCount > 0 || raidBossViewCount > 0)
    ) {
      removals.push({
        kind: "raid",
        name: raid.name,
        raidDayCount,
        bossViewCount: raidBossViewCount,
      });
    }

    for (const boss of raid.bosses) {
      const bossViewCount = Number(boss.bossViewCount);
      const assignmentCount = Number(boss.assignmentCount);
      if (
        !retainedBossIds.has(boss.id) &&
        (bossViewCount > 0 || assignmentCount > 0)
      ) {
        removals.push({
          kind: "boss",
          name: boss.name,
          raidName: raid.name,
          bossViewCount,
          assignmentCount,
        });
      }
    }
  }

  return removals;
}
